/**
 * Milestone 5, the team leaderboard: FACEIT and Leetify numbers next to the
 * team's own playbook progress. Teammates are compared with each other only.
 */

import { z } from "zod";

export const STAT_SOURCES = ["faceit", "leetify", "playbook", "published", "aim"] as const;
export type StatSource = (typeof STAT_SOURCES)[number];

/** FACEIT numbers over a period: lifetime, or the last matches. */
export interface FaceitPeriod {
  kd: number | null;
  hs: number | null;
  winRate: number | null;
  matches: number | null;
  adr: number | null;
}

export interface FaceitStats {
  nickname: string;
  elo: number | null;
  level: number | null;
  lifetime: FaceitPeriod;
  recent: FaceitPeriod;
}

/**
 * Leetify's numbers exactly as its public API returns them: its developer
 * guidelines forbid renaming, rescaling, reformatting or recalculating them,
 * and storing them (they are fetched live each time, see LeetifyBoard).
 */
export interface LeetifyStats {
  name: string;
  /** ranks.leetify, shown as "Leetify Rating". */
  leetifyRating: number | null;
  /** rating.aim, rating.positioning, rating.utility: 0–100, shown as given. */
  aim: number | null;
  positioning: number | null;
  utility: number | null;
  /** ranks.premier. */
  premier: number | null;
  /** total_matches. */
  totalMatches: number | null;
}

export interface PlaybookStats {
  tracked: number;
  learned: number;
  practising: number;
  /** Stuffs in the team's library. */
  teamTotal: number;
  /** Learned out of the team's library, in percent. */
  share: number;
  /** Executions this member is pinned in where every throw of their role is learned. */
  executionsReady: number;
  executionsPinned: number;
}

/** What a member put in the library: singles and combos they wrote, by map. */
export interface PublishedStats {
  total: number;
  singles: number;
  combos: number;
  /** Singles and combos on each map, maps without any left out. */
  byMap: Record<string, number>;
  /** Singles and combos that have a video: their own clip or a YouTube link. */
  videos: number;
  /** Pictures (stand, aim, landing…) on the singles they wrote. */
  pictures: number;
}

/** A player's mouse: entered by them, the same in every team. */
export interface AimSettings {
  dpi: number | null;
  sensitivity: number | null;
}

export const AimSettingsInput = z.object({
  dpi: z.number().int("DPI is a whole number.").min(50, "At least 50 DPI.").max(64000, "At most 64000 DPI.").nullable(),
  sensitivity: z.number().min(0.01, "At least 0.01.").max(50, "At most 50.").nullable(),
});
export type AimSettingsInput = z.infer<typeof AimSettingsInput>;

/** CS2's yaw: degrees turned per mouse count at sensitivity 1. */
export const CS2_YAW = 0.022;

/** eDPI: DPI × in-game sensitivity, the one number that compares two players' aim speed. */
export function edpi(a: AimSettings | undefined | null): number | null {
  if (!a?.dpi || !a.sensitivity) return null;
  return Math.round(a.dpi * a.sensitivity * 100) / 100;
}

/** How far the mouse travels for a full turn, in centimetres. */
export function cmPer360(e: number | null): number | null {
  if (!e) return null;
  return Math.round((360 / (CS2_YAW * e)) * 2.54 * 10) / 10;
}

export interface ExternalStats<T> {
  data: T | null;
  /** The player's page on FACEIT or Leetify. */
  url: string | null;
  fetchedAt: string | null;
  /** True when the player has no profile there (a dash in the table). */
  missing: boolean;
  /** The last lookup failed (network, rate limit). */
  failed?: boolean;
}

export interface LeaderboardMember {
  id: string;
  name: string;
  avatarUrl: string | null;
  me: boolean;
  /** Hidden from the others: only the member themselves sees their row. */
  hidden: boolean;
}

export interface Leaderboard {
  members: LeaderboardMember[];
  faceit: { configured: boolean; stats: Record<string, ExternalStats<FaceitStats>> };
  playbook: Record<string, PlaybookStats>;
  published: Record<string, PublishedStats>;
  aim: Record<string, AimSettings>;
  /** The oldest FACEIT refresh among the members shown, for "updated 3 h ago". */
  refreshedAt: string | null;
  /** A refresh is running: ask again in a few seconds. */
  refreshing: boolean;
}

/**
 * Leetify, fetched live for the members shown each time the tab asks: nothing
 * of it is kept on the server (Leetify's developer guidelines, data storage).
 */
export interface LeetifyBoard {
  stats: Record<string, ExternalStats<LeetifyStats>>;
  fetchedAt: string;
}

/** FACEIT stats are fetched again after this long, or on demand. */
export const STATS_MAX_AGE_MS = 24 * 3600_000;
/** On-demand FACEIT refresh at most this often per team. */
export const STATS_REFRESH_COOLDOWN_MS = 2 * 60_000;
